import React, { useContext, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import classNames from 'classnames';
import elvis from 'assets/img/elvis/elvis.png';
import { Remark } from 'react-remark';
import { useSpeak } from "react-text-to-speech";
import { ChatContext } from 'context/Context';
import { faPlay, faStop, faPause } from "@fortawesome/free-solid-svg-icons";
import SpeechControl from './buttons/SpeechControl';
import MessageRater from './buttons/MessageRater';
import ReportChat from './buttons/ReportChat';
import Highlight from '../Highlight';

const Message = ({ message, type, id, rating }) => {
  const { selectedVoice, autoPlay, searchQuery } = useContext(ChatContext);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const isLeft = type !== 'q';

  // Quick function to strip headers, bold, italics, and links
  const convertToPlainText = (markdown) => {
    return markdown
      .replace(/#+\s/g, '') // Removes headers (###)      
      .replace(/\*\*|__/g, '') // Removes bold (** or __)      
      .replace(/\*|_/g, '') // Removes italics (* or _)      
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // Keeps link text, removes URL  
  };

  // Normalize the incoming message to a plain markdown string for rendering
  const messageText = typeof message === 'string'
    ? message
    : (message && (message.answer || message.question || ''));

  // Remark rendering (force remount when `messageKey` changes so final markdown is re-parsed)
  const messageNode = useMemo(() => (
    <Remark>{(messageText || '').trim()}</Remark>
  ), [messageText]);

  const {
    //Text, // Component that returns the modified text property (kept for TTS highlighting if needed)
    speechStatus, // String that stores current speech status
    start, // Function to start the speech or put it in queue
    speak, // Function to start the speech or put it in queue
    pause, // Function to pause the speech
    stop, // Function to stop the speech or remove it from queue
  } = useSpeak({
    // Pass a plain string to the speech engine (not a React node)
    text: convertToPlainText(messageText),
    lang: 'en-GB',
    rate: 1.1,
    voiceURI: selectedVoice,
    stableText: true,
    showOnlyHighlightedText: false,
    highlightMode: "word",
    highlightProps: { style: { backgroundColor: "#c0c0c0", borderRadius: '5px' } },
  });

  useEffect(() => {
    if (autoPlay && speechStatus === "idle")
      speak({ text: messageText, voiceURI: selectedVoice });
  }, [autoPlay, speechStatus, speak, messageText, selectedVoice]);

  // Check for smaller screens
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 750);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showPlay = speechStatus === "idle" || speechStatus === "paused" || speechStatus === "stopped";
  return (
    <Flex className={classNames('p-3', { 'd-block': !isLeft })}>
      {(isLeft && !isSmallScreen) && <Avatar size="2xl" className="me-2 bg-primary rounded-circle" src={elvis} />}
      <div
        className={classNames('flex-1', {
          'd-flex justify-content-end': !isLeft
        })}
      >
        <div
          className={classNames('w-xxl-75', {
            'w-100': !isLeft || isSmallScreen
          })}
        >
          <Flex
            alignItems="center"
            className={classNames('hover-actions-trigger', {
              'flex-end-center': !isLeft,
              'align-items-center': isLeft
            })}
          >
            <div
              className={classNames('p-2 rounded-2 chat-message shadow-sm', {
                'bg-200': isLeft,
                'bg-primary text-white light': !isLeft,
              })}
            >
              <div className="mb-0">
                {type === 'a' ?
                  <>
                    {id > 0 && <ReportChat id={id} message={messageNode} />}
                    <div className="px-2 pt-3">
                      {messageNode}
                    </div>
                    <div className='float-end speech-btn-container'>
                      <SpeechControl
                        onclick={showPlay ? start : pause}
                        icon={showPlay ? faPlay : faPause}
                        colour={showPlay ? 'success' : 'primary'} />
                      <SpeechControl
                        onclick={stop}
                        icon={faStop}
                        colour='danger' />
                      {id > 0 && <MessageRater id={id} rating={rating} />}
                    </div>
                  </>
                  :
                  <Highlight
                    question={message}
                    query={searchQuery?.id == id ? searchQuery.query : null}
                  />
                }
              </div>
            </div>
          </Flex>
          <div
            className={classNames('text-400 fs--2', {
              'text-end': !isLeft
            })}
          >
          </div>
        </div>
      </div>
    </Flex>
  );
};
Message.propTypes = {
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  type: PropTypes.string,
  id: PropTypes.number,
  rating: PropTypes.number
};

export default Message;
